import React, {useState} from 'react';
import PropTypes from 'prop-types';
import PillLabel from '../../../../shared/library/buttons/base/pill-label';
import glamorous from 'glamorous';
import {BASE_TEXT, WEIGHT} from '../../../../shared/style/typography';
import {CHARCOAL, CONCRETE, TARMAC, WHITE} from '../../../../shared/style/colors';
import {Query} from 'react-apollo';
import Spinner, {BUTTON} from '../../../../shared/library/indicators/indeterminate/circular';
import {PHONE} from '../../../../shared/style/breakpoints';
import {useSendAnalyticsEvent} from '../../../../shared/enhancers/analytics-enhancer';
import PrivatePublicIndicator from '../../../../shared/library/private-public-indicator';
import {TOP_END} from '../../../../shared/constants/placements';
import Loader from '../loaders/loader';
import NavAnchor, {NAV_ANCHOR_OFFSET} from '../../../tool-profile/components/shared/nav-anchor';

const Container = glamorous.div({
  marginTop: 48,
  '&:first-child': {
    marginTop: 0
  },
  [PHONE]: {
    paddingLeft: 10,
    paddingRight: 10
  }
});

const Center = glamorous.div({
  display: 'flex',
  justifyContent: 'center',
  marginTop: 20,
  marginBottom: 20
});

const Title = glamorous.div({
  display: 'flex',
  alignItems: 'center',
  margin: 0,
  marginBottom: 13,
  position: 'inherit',
  ' h3': {
    ...BASE_TEXT,
    fontSize: 15,
    color: CHARCOAL,
    fontWeight: WEIGHT.BOLD,
    lineHeight: `18px`,
    margin: '-120px 0 0 0',
    paddingTop: 120
  },
  '>div': {
    marginLeft: 12
  }
});

const LoadMoreSpinner = glamorous(Spinner)({
  marginRight: 'auto',
  marginLeft: 'auto'
});

const Content = glamorous.div(
  {
    display: 'grid',
    gridColumnGap: 20,
    gridRowGap: 20,
    marginTop: 15,
    marginBottom: 26
  },
  ({gridCols}) => ({
    gridTemplateColumns: Array(gridCols)
      .fill('1fr')
      .join(' ')
  })
);

const ViewMore = glamorous.button({
  ...BASE_TEXT,
  height: 36,
  border: `1px solid ${CONCRETE}`,
  borderRadius: 36 / 2,
  background: WHITE,
  paddingLeft: 24,
  paddingRight: 24,
  fontSize: 11,
  lineHeight: 1,
  fontWeight: WEIGHT.BOLD,
  letterSpacing: 0.52,
  outline: 'none',
  minWidth: 120,
  zIndex: 1 // this puts the button above the negative margin for the section (used to nudge the anchor links position)
});

const updateQuery = (prev, next, dataKey) => {
  if (next.company[dataKey].edges.length) {
    next.company[dataKey].edges = [...prev.company[dataKey].edges, ...next.company[dataKey].edges];
    return next;
  } else {
    return prev;
  }
};

const Section = ({
  title = '',
  count,
  variables = {},
  data,
  query,
  dataKey,
  initialPageSize = 4,
  pageSize = 8,
  children,
  gridCols = 2,
  customTitleComponent,
  middleDetail,
  privateMode = false
}) => {
  const [showAll, setShowAll] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [actualCount, setActualCount] = useState(count);
  const sendAnalyticsEvent = useSendAnalyticsEvent();

  const changeDataHandler = values => {
    setActualCount(values.count ? values.count : 0);
  };

  return (
    <Container>
      <NavAnchor id={title.toLowerCase().replace(' ', '-')} offset={NAV_ANCHOR_OFFSET} />
      <Title>
        <h3>
          {title} <PillLabel flavour={TARMAC}>{actualCount}</PillLabel>{' '}
        </h3>
        {privateMode && (
          <PrivatePublicIndicator typeIndicator="BlueRoundLarge" position={TOP_END} />
        )}
        {customTitleComponent && customTitleComponent}
      </Title>
      {middleDetail && middleDetail}
      {data ? (
        <>
          <Content gridCols={gridCols}>
            {children({
              edges: showAll ? data : data.slice(0, Math.min(actualCount, initialPageSize))
            })}
          </Content>
          {!showAll && (
            <Center>
              <ViewMore onClick={() => setShowAll(true)}>VIEW ALL</ViewMore>
            </Center>
          )}
        </>
      ) : (
        <Query query={query} variables={{...variables, first: initialPageSize, after: null}}>
          {resp => {
            if (resp.loading && !(resp.data && resp.data.company)) {
              return (
                <Content gridCols={gridCols}>
                  <Loader gridCols={gridCols} elementsLength={initialPageSize} dataKey={dataKey} />
                </Content>
              );
            }

            if (resp.data && resp.data.company) {
              changeDataHandler(resp.data.company[dataKey]);
              return (
                <>
                  <Content gridCols={gridCols}>{children(resp.data.company[dataKey])}</Content>
                  {resp.data.company[dataKey].pageInfo.hasNextPage && (
                    <Center>
                      <ViewMore
                        disabled={loadingMore}
                        onClick={() => {
                          sendAnalyticsEvent('click_viewMore', {section: dataKey});
                          setLoadingMore(true);
                          resp.fetchMore({
                            variables: {
                              first: pageSize,
                              after: resp.data.company[dataKey].pageInfo.endCursor
                            },
                            updateQuery: (prev, {fetchMoreResult: next}) => {
                              setLoadingMore(false);
                              return updateQuery(prev, next, dataKey);
                            }
                          });
                        }}
                      >
                        {loadingMore ? <LoadMoreSpinner size={BUTTON} /> : 'VIEW MORE'}
                      </ViewMore>
                    </Center>
                  )}
                </>
              );
            }

            return 'Unable to fetch the data, please try again after some time!';
          }}
        </Query>
      )}
    </Container>
  );
};

Section.propTypes = {
  title: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
  variables: PropTypes.object,
  query: PropTypes.object,
  dataKey: PropTypes.string,
  data: PropTypes.array,
  initialPageSize: PropTypes.number,
  pageSize: PropTypes.number,
  gridCols: PropTypes.number,
  children: PropTypes.any,
  customTitleComponent: PropTypes.any,
  middleDetail: PropTypes.object,
  privateMode: PropTypes.bool
};

export default Section;
